import type { Messages } from "../../../core/api";

// Pure layout logic for the agency review-document screen. It mirrors the server PDF generator
// (`mfDocumentAdapter.ts`: recursivelyChunkQuestionAnswersToRows / shouldDisplayQuestion) so the
// on-screen preview shows the same questions, packed the same way, as the generated PDF.
// Only `import type` from core/api is allowed here so the module loads without the app runtime.

export type ContentItem = Messages["PatientDocumentContentItem"];
export type AnswerObject = Messages["PatientDocumentAnswerObject"];
export type AnswersByQuestion = Map<number, AnswerObject[]>;
export type BlockWidth = 1 | 2 | 3 | 4;

export interface LayoutBlock {
  answers: AnswerObject[];
  question: ContentItem;
  /** null means the block fills the whole row */
  width: BlockWidth | null;
}

export interface LayoutRow {
  blocks: LayoutBlock[];
}

export interface PlaceholderValues {
  agencyAddress: string | null;
  agencyName: string;
  agencyPhone: string | null;
  caregiverName: string | null;
  patientName: string;
}

const MAX_COLUMNS_PER_ROW = 4;

/**
 * Item types the server PDF generator renders as nothing (convertQuestionAnswerToMfBlock).
 * medicationProfile is not one: before rendering, the server turns it into a chart of the
 * patient's medications, or a text when they take none (fillMedicationProfileAnswers).
 */
const NOT_RENDERED_ON_PDF = new Set<ContentItem["itemType"]>([
  "vbpItem",
  "medicationProfileItem",
  "RNPlatformPatientPhysician",
]);

const NOT_COMMENTABLE = new Set<ContentItem["itemType"]>([
  "image",
  "editor",
  "bigHeader",
  "smallHeader",
  "patientPhysician",
  "RNPlatformPatientPhysician",
  "vbpItem",
]);

const INLINE_EDITABLE = new Set<ContentItem["itemType"]>([
  "textShort",
  "textLong",
  "number",
  "date",
  "time",
  "radio",
  "check",
  "yesNo",
  "dropDown",
  "bloodPressure",
  "weight",
  "employeeSignature",
  "patientSignature",
  "customSignature",
  "chart",
  "bodyDrawing",
  "POC",
  "RNPlatformPatientPhysician",
  // Entity-backed: their editor changes the patient's record (see ENTITY_BACKED)
  "medicationProfile",
  "icdCodes",
  "finalIcdCodes",
  "sectionedIcdCodes",
  "medicalDiagnosis",
  "patientPhysician",
]);

/** Choice types whose parentAnswer sync (exclusive option + parent answer) the editor replicates. */
const PARENT_ANSWER_EDITABLE = new Set<ContentItem["itemType"]>(["radio", "check", "dropDown"]);

/** Types backed by a patient record rather than the answer: view-only inside a chart row. */
const ENTITY_BACKED = new Set<ContentItem["itemType"]>([
  "medicationProfile",
  "icdCodes",
  "finalIcdCodes",
  "sectionedIcdCodes",
  "medicalDiagnosis",
  "patientPhysician",
]);

/** Backed by a patient record (medications, ICD / medical diagnosis codes, physician). */
export function isEntityBacked(question: ContentItem): boolean {
  return ENTITY_BACKED.has(question.itemType);
}

function childrenOf(question: ContentItem): ContentItem[] {
  return question.columns?.[0] ?? [];
}

export function indexAnswers(answers: AnswerObject[]): AnswersByQuestion {
  const byQuestion: AnswersByQuestion = new Map();
  for (const answer of answers) {
    const list = byQuestion.get(answer.questionId);
    if (list === undefined) {
      byQuestion.set(answer.questionId, [answer]);
    } else {
      list.push(answer);
    }
  }
  for (const list of byQuestion.values()) {
    list.sort((a, b) => a.chartRow - b.chartRow);
  }
  return byQuestion;
}

export function flattenContent(content: ContentItem[]): ContentItem[] {
  const flat: ContentItem[] = [];
  for (const item of content) {
    flat.push(item);
    flat.push(...flattenContent(childrenOf(item)));
  }
  return flat;
}

export function isAnswerEmpty(answers: AnswerObject[]): boolean {
  // Like the server, only the first answer decides.
  const answer: unknown = answers[0]?.answer;
  return (
    answer === null ||
    answer === undefined ||
    answer === "" ||
    (Array.isArray(answer) && answer.length === 0)
  );
}

export function shouldDisplayQuestion(
  question: ContentItem,
  byId: Map<number, ContentItem>,
  answers: AnswersByQuestion
): boolean {
  if (question.parentId === null) {
    return true;
  }
  const parent = byId.get(question.parentId);
  if (parent === undefined) {
    return true;
  }

  if (question.showIfParentEquals === true) {
    const parentAnswer = answers.get(parent.id)?.[0];
    if (parentAnswer !== undefined && question.ifParentEquals !== undefined) {
      const value: unknown = parentAnswer.answer;
      if (Array.isArray(value)) {
        if (!(value as unknown[]).includes(question.ifParentEquals)) {
          return false;
        }
      } else if (value !== question.ifParentEquals) {
        return false;
      }
    }
    return true;
  }

  return shouldDisplayQuestion(parent, byId, answers);
}

function toBlockWidth(question: ContentItem): BlockWidth | null {
  if (question.itemType === "bigHeader") {
    return null;
  }
  return question.width ?? null;
}

/** Mirrors recursivelyChunkQuestionAnswersToRows; chart children still take up columns. */
function chunk(
  items: ContentItem[],
  byId: Map<number, ContentItem>,
  answers: AnswersByQuestion,
  rows: ContentItem[][],
  state: { current: number }
): void {
  for (const question of items) {
    const width = toBlockWidth(question) ?? MAX_COLUMNS_PER_ROW;

    if (!shouldDisplayQuestion(question, byId, answers)) {
      continue;
    }

    if (question.hideOnPDF === true) {
      chunk(childrenOf(question), byId, answers, rows, state);
      continue;
    }

    const isFullLine = width === MAX_COLUMNS_PER_ROW;
    const willOverflow = width + state.current > MAX_COLUMNS_PER_ROW;
    if (isFullLine || willOverflow || question.forceNewLine === true) {
      rows.push([question]);
      state.current = width;
    } else {
      if (state.current === 0 || rows.length === 0) {
        rows.push([question]);
      } else {
        rows[rows.length - 1].push(question);
      }
      state.current += width;
    }

    if (question.itemType !== "customSignature") {
      chunk(childrenOf(question), byId, answers, rows, state);
    }
  }
}

function isBlockRendered(question: ContentItem, answers: AnswersByQuestion): boolean {
  // Children of a chart are rendered by the chart itself.
  if (question.parentType === "chart") {
    return false;
  }
  if (question.hideOnPdfIfEmpty === true && isAnswerEmpty(answers.get(question.id) ?? [])) {
    return false;
  }
  return !NOT_RENDERED_ON_PDF.has(question.itemType);
}

export function layoutRows(content: ContentItem[], answers: AnswersByQuestion): LayoutRow[] {
  const byId = new Map(flattenContent(content).map((item) => [item.id, item]));
  const chunked: ContentItem[][] = [];
  chunk(content, byId, answers, chunked, { current: 0 });

  const rows: LayoutRow[] = [];
  for (const row of chunked) {
    const blocks = row
      .filter((question) => isBlockRendered(question, answers))
      .map((question) => ({
        question,
        width: toBlockWidth(question),
        answers: answers.get(question.id) ?? [],
      }));
    if (blocks.length > 0) {
      rows.push({ blocks });
    }
  }
  return rows;
}

export function hasParentAnswer(question: ContentItem): boolean {
  return question.parentAnswer !== undefined && question.parentAnswer !== null;
}

/** mobile hasChildrenWithParentAnswer: such a parent is not shown, its children replace it. */
export function hasChildrenWithParentAnswer(question: ContentItem): boolean {
  return childrenOf(question).some(hasParentAnswer);
}

/**
 * The template-document question list. Like mobile, a parent whose children have `parentAnswer`
 * is replaced by those children, and they follow the parent's visibility rather than their own
 * `showIfParentEquals` on it (they compute the parent's answer, so they must stay reachable: a
 * parent holding the exclusive value would otherwise hide the child that shows it).
 */
export function listQuestions(content: ContentItem[], answers: AnswersByQuestion): ContentItem[] {
  const byId = new Map(flattenContent(content).map((item) => [item.id, item]));
  const list: ContentItem[] = [];
  const visit = (items: ContentItem[], parentReplacedByChildren: boolean) => {
    for (const question of items) {
      // The replaced parent's own visibility was checked before visiting its children.
      const followsParent = parentReplacedByChildren && hasParentAnswer(question);
      if (!followsParent && !shouldDisplayQuestion(question, byId, answers)) {
        continue;
      }
      const isReplaced = hasChildrenWithParentAnswer(question);
      if (!isReplaced && question.hideOnPDF !== true && question.parentType !== "chart") {
        list.push(question);
      }
      visit(childrenOf(question), isReplaced);
    }
  };
  visit(content, false);
  return list;
}

/**
 * The question whose block shows `questionId`: itself, or the closest ancestor that has a block
 * (chart rows are drawn by their chart). A parent replaced by its parentAnswer children (template
 * list, see `listQuestions`) is shown by its first shown parentAnswer child. Null when no block
 * shows it (hidden or not printed).
 */
export function findShownQuestionId(
  questionId: number,
  byId: Map<number, ContentItem>,
  shownIds: ReadonlySet<number>
): number | null {
  let current = byId.get(questionId);
  while (current !== undefined) {
    if (shownIds.has(current.id)) {
      return current.id;
    }
    const shownChild = childrenOf(current).find(
      (child) => hasParentAnswer(child) && shownIds.has(child.id)
    );
    if (shownChild !== undefined) {
      return shownChild.id;
    }
    current = current.parentId !== null ? byId.get(current.parentId) : undefined;
  }
  return null;
}

export function replacePlaceholders(text: string, values: PlaceholderValues): string {
  const replacements: [RegExp, string | null][] = [
    [/{agency_name}/gi, values.agencyName],
    [/{patient_name}/gi, values.patientName],
    [/{caregiver_name}/gi, values.caregiverName],
    [/{agency_address}/gi, values.agencyAddress],
    [/{agency_phone}/gi, values.agencyPhone],
  ];
  return replacements.reduce(
    (result, [pattern, value]) => result.replace(pattern, () => value ?? ""),
    text
  );
}

/**
 * Ids of the questions the RN never sees on mobile: every `hideOnMobile` question with its whole
 * subtree. Mirrors the server's `filterHideOnMobileQuestions`, which drops hidden items (and
 * everything nested under them in `columns[0]`) before the document reaches the app.
 */
export function mobileHiddenQuestionIds(content: ContentItem[]): Set<number> {
  const hidden = new Set<number>();
  const visit = (items: ContentItem[], isUnderHidden: boolean) => {
    for (const item of items) {
      const isHidden = isUnderHidden || item.hideOnMobile === true;
      if (isHidden) {
        hidden.add(item.id);
      }
      visit(childrenOf(item), isHidden);
    }
  };
  visit(content, false);
  return hidden;
}

/** `mobileHiddenIds` comes from `mobileHiddenQuestionIds` over the same document content. */
export function isCommentable(
  question: ContentItem,
  mobileHiddenIds: ReadonlySet<number>
): boolean {
  return (
    question.hideOnMobile !== true &&
    !mobileHiddenIds.has(question.id) &&
    !NOT_COMMENTABLE.has(question.itemType)
  );
}

export function isInlineEditable(question: ContentItem): boolean {
  // A question with `parentAnswer` syncs its parent's answer on save (mobile); the choice editor
  // replicates that for radio / check / dropDown only.
  if (hasParentAnswer(question)) {
    return PARENT_ANSWER_EDITABLE.has(question.itemType);
  }
  return INLINE_EDITABLE.has(question.itemType);
}

/**
 * A cell of a chart row editable in the chart editor: an inline-editable answer type, except
 * entity-backed types (view-only in a chart), nested charts and parentAnswer children (which sync
 * a parent outside the row).
 */
export function isChartCellEditable(question: ContentItem): boolean {
  return (
    isInlineEditable(question) &&
    question.itemType !== "chart" &&
    !hasParentAnswer(question) &&
    !isEntityBacked(question)
  );
}
